import { LinkCore } from 'solito/link'
import { ComponentType } from 'react'

type SmartLinkProps<C extends ComponentType<any>> = {
  Component: C
  href?: string
  rel?: string
  shallow?: boolean
  replace?: boolean
  children?: React.ReactNode
} & React.ComponentProps<C>

const makeLinkSafe = (href: string) => {
  const prefixes = [
    'http://',
    'https://',
    'mailto:',
    'tel:',
    'ftp://',
    'ftps://',
    'sftp://',
    'ssh://',
    'file://',
    'mythical-society://',
  ]

  if (prefixes.some((prefix) => href.startsWith(prefix))) {
    return href
  }

  if (href.startsWith('/')) {
    return href
  }

  if (href.startsWith('#')) {
    return href
  }

  return `/${href}`
}

const makeRel = (target: string, rel?: string) => {
  const relParts = new Set((rel || '').split(' ').filter(Boolean))

  if (target === '_blank') {
    relParts.add('noopener')
  }

  return relParts.size > 0 ? Array.from(relParts).join(' ') : undefined
}

/**
 * Wrapper around LinkCore that allows to use any component as a link.
 * If href is not provided, it will render the component without a link.
 * <SmartLink Component={Button} margin="$2" href="/myPage">Example</SmartLink>
 */
export const SmartLink = <C extends ComponentType<any>>({
  Component,
  href,
  rel,
  children,
  shallow,
  replace,
  ...props
}: SmartLinkProps<C>) => {
  if (!href) return <Component {...props}>{children}</Component>

  return (
    <LinkCore
      Component={Component}
      href={makeLinkSafe(href)}
      shallow={shallow}
      replace={replace}
      componentProps={{
        accessibilityRole: 'link',
        tag: 'a',
        rel: makeRel(props.target, props.rel),
        cursor: 'pointer',
        onClick: (e) => {
          if (e.defaultPrevented) return
        },
        ...props,
      }}
    >
      {children}
    </LinkCore>
  )
}
